/**
 * Servicio de administración para operaciones de gestión de usuarios
 * Centraliza las llamadas a Supabase para funciones de administrador
 */

import { supabase } from '../config/supabase';

/**
 * Obtiene usuarios con paginación cursor-based eficiente
 * Solo accesible para usuarios con rol admin (level >= 100)
 * @param {object} params - Parámetros de paginación y búsqueda
 * @param {number} params.limit - Cantidad de registros por página (default: 99)
 * @param {string} params.cursorId - ID del cursor para paginación (opcional)
 * @param {string} params.searchText - Texto para buscar por nombre o email (opcional)
 * @param {string} params.orderBy - Campo de ordenamiento: 'updated_at', 'created_at', 'email' (default: 'updated_at')
 * @returns {Promise<object>} - Objeto con usuarios, next_cursor y total_count
 * @throws {Error} - Si no tiene permisos de admin o hay error en la llamada
 */
export const getAdminUsersPaginated = async ({ 
  limit = 99, 
  cursorId = null, 
  searchText = null, 
  orderBy = 'updated_at' 
} = {}) => {
  try {
    const { data, error } = await supabase.rpc('admin_get_users_paginated', {
      p_limit: limit,
      p_cursor_id: cursorId,
      p_search_text: searchText,
      p_order_by: orderBy
    });

    if (error) {
      console.error('Error en getAdminUsersPaginated:', error);
      throw new Error(error.message);
    }

    // Extraer el cursor de la última fila para la siguiente página
    const users = data || [];
    const lastUser = users[users.length - 1];
    const nextCursor = lastUser?.next_cursor || null;
    const totalCount = users[0]?.total_count || 0;

    // Remover el campo next_cursor de cada usuario antes de retornar
    const cleanUsers = users.map(({ next_cursor, total_count, ...user }) => user);

    return {
      users: cleanUsers,
      nextCursor,
      totalCount,
      hasMore: nextCursor !== null
    };
  } catch (error) {
    console.error('Error obteniendo usuarios paginados:', error);
    throw error;
  }
};

/**
 * Obtiene todos los datos de un usuario específico (sin paginación)
 * Solo accesible para usuarios con rol admin (level >= 100)
 * @param {string} userId - ID del usuario a consultar
 * @returns {Promise<object>} - Datos completos del usuario
 * @throws {Error} - Si no tiene permisos de admin o hay error en la llamada
 */
export const getAdminUserData = async (userId) => {
  try {
    const { data, error } = await supabase.rpc('admin_get_all_user_data');

    if (error) {
      console.error('Error en getAdminUserData:', error);
      throw new Error(error.message);
    }

    // Filtrar por el ID específico
    const user = data?.find(u => u.id === userId);

    if (!user) {
      throw new Error('Usuario no encontrado');
    }

    return user;
  } catch (error) {
    console.error('Error obteniendo datos del usuario:', error);
    throw error;
  }
};

/**
 * Cambia la contraseña de un usuario (función admin)
 * Solo accesible para usuarios con rol admin (level >= 100)
 * @param {string} userId - ID del usuario
 * @param {string} newPassword - Nueva contraseña
 * @returns {Promise<object>} - Resultado de la operación
 * @throws {Error} - Si no tiene permisos de admin o hay error en la llamada
 */
export const adminChangeUserPassword = async (userId, newPassword) => {
  try {
    const { data, error } = await supabase.rpc('admin_change_user_password', {
      user_id: userId,
      new_password: newPassword
    });

    if (error) {
      console.error('Error en adminChangeUserPassword:', error);
      throw new Error(error.message);
    }

    return { success: true };
  } catch (error) {
    console.error('Error cambiando contraseña de usuario:', error);
    throw error;
  }
};
